import { gql } from '@apollo/client'

export interface IPrepItemWeekdayConfig {
  prepItemIdCode: string
  prepItemName: string
  scheduleName: string
  isActivated: boolean
  sunday: boolean
  monday: boolean
  tuesday: boolean
  wednesday: boolean
  thursday: boolean
  friday: boolean
  saturday: boolean
}

export interface INextWeekPrepItemRequirement {
  locationId: number
  locationCode: string
  locationName: string
  dayOfWeek: number
  prepItemIdCode: string
  prepItemName: string
  prepItemUnit: string
  unitAmountOfEachPrepItemNeeded: number
  salesForecast: number
  forecastDate: string
}

export interface INextWeekPrepItemRequirementsType {
  getNextWeekPrepItemRequirementsByDow: {
    nodes: INextWeekPrepItemRequirement[]
  }
}

export interface INextWeekPrepItemRequirementsVariables {
  locationId: number
}

export const getNextWeekPrepItemRequirementsByDow = gql`
  query GetNextWeekPrepItemRequirementsByDow($locationId: Int!) {
    getNextWeekPrepItemRequirementsByDow(iLocationId: $locationId) {
      nodes {
        locationId
        locationCode
        locationName
        dayOfWeek
        prepItemIdCode
        prepItemName
        prepItemUnit
        forecastDate
        salesForecast
        unitAmountOfEachPrepItemNeeded
      }
    }
  }
`

export interface IPrepItemBatchStandard {
  prepItemIdCode: string
  prepItemName: string
  prepBatchType: string
  unit: string
  standardUnitsPerBatch: number
  sourceType: 'PREP' | 'ONHAND'
  batchTypeId: number
  unitId: number
}

export interface IPrepItemBatchStandardsType {
  getPrepItemBatchStandards: {
    nodes: IPrepItemBatchStandard[]
  }
}

export const getPrepItemBatchStandards = gql`
  query GetPrepItemBatchStandards {
    getPrepItemBatchStandards {
      nodes {
        sourceType
        prepItemIdCode
        prepItemName
        prepBatchType
        batchTypeId
        unit
        unitId
        standardUnitsPerBatch
      }
    }
  }
`

export interface IPrepItemSettings {
  prepItemIdCode: string
  displayOnHandBatchTypeId: number
  displayOnHandSourceType: string
  displayOnHandBatchType: string
  restDays: number
  minimumUnit: number
}

export interface IPrepItemSettingsType {
  getPrepItemSettings: {
    nodes: IPrepItemSettings[]
  }
}

export const getPrepItemSettings = gql`
  query GetPrepItemSettings {
    getPrepItemSettings {
      nodes {
        prepItemIdCode
        displayOnHandBatchTypeId
        displayOnHandSourceType
        displayOnHandBatchType
        restDays
        minimumUnit
      }
    }
  }
`

export interface IRecordDataType {
  items: {
    prep: number
    needed: number
    onHand: number
    onHandUnit: {
      type: string
      standardUnitPerUnit: number
    } | null
    prepItemIdCode: string
    prepItemName: string
    prepItemUnit: string
    prepBatchType: string
    standardUnitsPerBatch: number
    onHandDetails: {
      quantity: number
      batchType: string
      standardUnitsPerBatch: number
    }[]
    unitAmountNeeded: number
    adjustedUnitAmountNeeded: number
    minimumUnit: number
  }[]
  nonPrepItems?: {
    prep: number
    needed: number
    onHand: number
    onHandUnit: {
      type: string
      standardUnitPerUnit: number
    } | null
    prepItemIdCode: string
    prepItemName: string
    prepItemUnit: string
    prepBatchType: string
    standardUnitsPerBatch: number
    onHandDetails: {
      quantity: number
      batchType: string
      standardUnitsPerBatch: number
    }[]
    unitAmountNeeded: number
    adjustedUnitAmountNeeded: number
    minimumUnit: number
  }[]
  weeklySalesForecast: number
  adjustment: number
  baseForecast: number
  onHandValues: {
    [key: string]: {
      [key: string]: number
    }
  }
  adjustedForecast: number
  adjustmentPercent?: number
}

export interface ISubmitPrepItemRecordVariables {
  iLocationId: number
  iReportDate: string
  iExpoUserId: number
  iRecord: IRecordDataType
}

export const submitPrepItemRecord = gql`
  mutation SubmitPrepItemRecord(
    $iLocationId: Int!
    $iReportDate: Date!
    $iExpoUserId: Int!
    $iRecord: JSON!
  ) {
    submitPrepItemRecord(
      input: {
        iLocationId: $iLocationId
        iReportDate: $iReportDate
        iExpoUserId: $iExpoUserId
        iRecord: $iRecord
      }
    ) {
      clientMutationId
    }
  }
`

export interface IPrepItemCategory {
  prepItemIdCode: string
  prepItemName: string
  parentPrepCategoryIdCode: string
}

export interface IPrepItemCategoryType {
  getPrepItemCategory: {
    nodes: IPrepItemCategory[]
  }
}

export const getPrepItemCategory = gql`
  query GetPrepItemCategory {
    getPrepItemCategory {
      nodes {
        prepItemIdCode
        prepItemName
        parentPrepCategoryIdCode
      }
    }
  }
`

// Delivery Day Scheduling — config_2 with location support
export interface IPrepItemWeekdayConfig2 {
  prepItemIdCode: string
  prepItemName: string
  scheduleName: string
  isActivated: boolean
  sunday: boolean
  monday: boolean
  tuesday: boolean
  wednesday: boolean
  thursday: boolean
  friday: boolean
  saturday: boolean
}

export interface IPrepItemWeekdayConfig2Type {
  getPrepItemWeekdayConfig2: {
    nodes: IPrepItemWeekdayConfig2[]
  }
}

export const getPrepItemWeekdayConfig2 = gql`
  query GetPrepItemWeekdayConfig2ForCalc(
    $iQueryDate: Date!
    $iLocationId: Int
  ) {
    getPrepItemWeekdayConfig2(
      iQueryDate: $iQueryDate
      iLocationId: $iLocationId
    ) {
      nodes {
        prepItemIdCode
        prepItemName
        scheduleName
        isActivated
        sunday
        monday
        tuesday
        wednesday
        thursday
        friday
        saturday
      }
    }
  }
`

// store_prep_schedule with full fallback chain
export interface IStorePrepScheduleCalc {
  prepItemIdCode: string
  prepItemName: string
  scheduleName: string
  isActivated: boolean
  sunday: boolean
  monday: boolean
  tuesday: boolean
  wednesday: boolean
  thursday: boolean
  friday: boolean
  saturday: boolean
  source: string
}

export interface IStorePrepScheduleCalcType {
  getStorePrepSchedule: {
    nodes: IStorePrepScheduleCalc[]
  }
}

export const getStorePrepScheduleForCalc = gql`
  query GetStorePrepScheduleForCalc($iQueryDate: Date!, $iLocationId: Int!) {
    getStorePrepSchedule(iQueryDate: $iQueryDate, iLocationId: $iLocationId) {
      nodes {
        prepItemIdCode
        prepItemName
        scheduleName
        isActivated
        sunday
        monday
        tuesday
        wednesday
        thursday
        friday
        saturday
        source
      }
    }
  }
`

export interface IStoreDeliveryDay {
  locationId: number
  locationName: string
  locationCode: string
  deliveryDays: string
}

export interface IStoreDeliveryDaysType {
  getDeliveryScheduleLocations: {
    nodes: IStoreDeliveryDay[]
  }
}

export const getDeliveryScheduleLocations = gql`
  query GetDeliveryScheduleLocationsForCalc {
    getDeliveryScheduleLocations {
      nodes {
        locationId
        locationName
        locationCode
        deliveryDays
      }
    }
  }
`

export interface IDeliveryDayPrepItem {
  prepItemId: string
  prepItemName: string
}

export interface IDeliveryDayPrepItemsType {
  getDeliveryDayPrepItems: {
    nodes: IDeliveryDayPrepItem[]
  }
}

export const getDeliveryDayPrepItems = gql`
  query GetDeliveryDayPrepItemsForCalc {
    getDeliveryDayPrepItems {
      nodes {
        prepItemId
        prepItemName
      }
    }
  }
`

export interface INextWeekTotalSalesForecastVariables {
  locationId: number
  queryDate: string
}

export interface IPrepItemSubmitRecord {
  locationId: number
  reportDate: string
  expoUserId?: number
  record: IRecordDataType
}

export interface IPrepItemSubmitRecordType {
  getPrepItemSubmitRecords: {
    nodes: IPrepItemSubmitRecord[]
  }
}

export const getPrepItemSubmitRecords = gql`
  query GetPrepItemSubmitRecords(
    $locationId: Int!
    $reportDate: Date!
    $expoUserId: Int
  ) {
    getPrepItemSubmitRecords(
      iLocationId: $locationId
      iReportDate: $reportDate
      iExpoUserId: $expoUserId
    ) {
      nodes {
        locationId
        reportDate
        expoUserId
        record
      }
    }
  }
`
export interface INextWeekTotalSalesForecastNode {
  queryDate: string
  locationId: number
  weeklySalesForecast: number
}

export interface INextWeekTotalSalesForecastType {
  getNextWeekTotalSalesForecast: {
    nodes: INextWeekTotalSalesForecastNode[]
  }
}

export const getNextWeekTotalSalesForecast = gql`
  query GetNextWeekTotalSalesForecast($locationId: Int!, $queryDate: Date!) {
    getNextWeekTotalSalesForecast(
      iLocationId: $locationId
      iQueryDate: $queryDate
    ) {
      nodes {
        queryDate
        locationId
        weeklySalesForecast
      }
    }
  }
`

export interface IPrepSettingsByDate {
  settingName: string
  settingValue: number
}

export interface IPrepSettingsByDateType {
  getPrepSettingsByDate: {
    nodes: IPrepSettingsByDate[]
  }
}

export interface IPrepSettingsByDateVariables {
  iQueryDate: string
  iLocationId: number
}

export const getPrepSettingsByDate = gql`
  query GetPrepSettingsByDate($iQueryDate: Date!, $iLocationId: Int!) {
    getPrepSettingsByDate(iQueryDate: $iQueryDate, iLocationId: $iLocationId) {
      nodes {
        settingName
        settingValue
      }
    }
  }
`
